import { DEFAULT_EVENT_FILTERS } from '@/types/events';
import type { ChargebeeEvent, DetailTab, EventFilters } from '@/types/events';
import type { ForwardMode } from '@/constant/eventForwardSamples';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

export interface ForwardConfig {
  mode: ForwardMode;
  proxyUrl: string;
  targetUrl: string;
}

interface EventExplorerState {
  // Event list
  events: ChargebeeEvent[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;

  // Pagination
  nextOffset: string | null;
  hasMore: boolean;

  // Selection
  selectedEventId: string | null;
  selectedEventDetail: ChargebeeEvent | null;
  isLoadingDetail: boolean;

  // Polling / Live tail
  pollingEnabled: boolean;
  liveBuffer: ChargebeeEvent[];
  lastPolledAt: number | null;
  nextPollAt: number | null;
  consecutiveErrors: number;

  // Filters
  filters: EventFilters;
  sortDirection: 'asc' | 'desc';

  // Detail panel
  activeDetailTab: DetailTab;

  // PII
  piiRedactionEnabled: boolean;

  // Auto-forward
  autoForwardEnabled: boolean;
  autoForwardStats: { total: number; succeeded: number; failed: number };

  // Forward config (shared between auto-forward and per-event forward)
  forwardConfig: ForwardConfig;

  // Actions
  setEvents: (events: ChargebeeEvent[]) => void;
  appendEvents: (events: ChargebeeEvent[], nextOffset: string | null) => void;
  setSelectedEventId: (id: string | null) => void;
  setSelectedEventDetail: (event: ChargebeeEvent | null) => void;
  setIsLoadingDetail: (loading: boolean) => void;
  setFilters: (filters: Partial<EventFilters>) => void;
  resetFilters: () => void;
  setSortDirection: (dir: 'asc' | 'desc') => void;
  setPollingEnabled: (enabled: boolean) => void;
  addToLiveBuffer: (events: ChargebeeEvent[]) => void;
  mergeLiveBuffer: () => void;
  setLastPolledAt: (ts: number | null) => void;
  setNextPollAt: (ts: number | null) => void;
  setConsecutiveErrors: (count: number) => void;
  setActiveDetailTab: (tab: DetailTab) => void;
  setPiiRedactionEnabled: (enabled: boolean) => void;
  setAutoForwardEnabled: (enabled: boolean) => void;
  incrementAutoForwardStats: (succeeded: boolean) => void;
  resetAutoForwardStats: () => void;
  setForwardConfig: (config: ForwardConfig) => void;
  setError: (error: string | null) => void;
  setIsLoading: (loading: boolean) => void;
  setIsLoadingMore: (loading: boolean) => void;
  setNextOffset: (offset: string | null) => void;
  setHasMore: (hasMore: boolean) => void;
  reset: () => void;
}

const MAX_LIVE_BUFFER_SIZE = 200;

const initialState = {
  events: [],
  isLoading: false,
  isLoadingMore: false,
  error: null,
  nextOffset: null,
  hasMore: true,
  selectedEventId: null,
  selectedEventDetail: null,
  isLoadingDetail: false,
  pollingEnabled: true,
  liveBuffer: [],
  lastPolledAt: null,
  nextPollAt: null,
  consecutiveErrors: 0,
  filters: { ...DEFAULT_EVENT_FILTERS },
  sortDirection: 'desc' as const,
  activeDetailTab: 'json' as DetailTab,
  piiRedactionEnabled: false,
  autoForwardEnabled: false,
  autoForwardStats: { total: 0, succeeded: 0, failed: 0 },
  forwardConfig: { mode: 'proxy' as ForwardMode, proxyUrl: '', targetUrl: '' },
};

export const useEventExplorerStore = create<EventExplorerState>()(
  subscribeWithSelector((set) => ({
    ...initialState,

    setEvents: (events) => set({ events }),

    appendEvents: (newEvents, nextOffset) =>
      set((state) => ({
        events: [...state.events, ...newEvents],
        nextOffset,
        hasMore: nextOffset != null,
        isLoadingMore: false,
      })),

    setSelectedEventId: (id) =>
      set((state) => {
        // Skip reset if selecting the already-selected event to avoid
        // clearing detail while the effect won't re-fire (same dep value).
        if (id === state.selectedEventId) return state;
        return {
          selectedEventId: id,
          selectedEventDetail: null,
          isLoadingDetail: !!id,
        };
      }),

    setSelectedEventDetail: (event) =>
      set({ selectedEventDetail: event, isLoadingDetail: false }),

    setIsLoadingDetail: (loading) => set({ isLoadingDetail: loading }),

    setFilters: (partial) =>
      set((state) => ({
        filters: { ...state.filters, ...partial },
        events: [],
        nextOffset: null,
        hasMore: true,
        selectedEventId: null,
        selectedEventDetail: null,
        liveBuffer: [],
      })),

    resetFilters: () =>
      set({
        filters: { ...DEFAULT_EVENT_FILTERS },
        events: [],
        nextOffset: null,
        hasMore: true,
        selectedEventId: null,
        selectedEventDetail: null,
        liveBuffer: [],
      }),

    setSortDirection: (dir) =>
      set({
        sortDirection: dir,
        events: [],
        nextOffset: null,
        hasMore: true,
        selectedEventId: null,
        selectedEventDetail: null,
        liveBuffer: [],
      }),

    setPollingEnabled: (enabled) =>
      set({ pollingEnabled: enabled, nextPollAt: null }),

    addToLiveBuffer: (newEvents) =>
      set((state) => {
        const combined = [...newEvents, ...state.liveBuffer];
        return {
          liveBuffer: combined.slice(0, MAX_LIVE_BUFFER_SIZE),
        };
      }),

    mergeLiveBuffer: () =>
      set((state) => ({
        events: [...state.liveBuffer, ...state.events],
        liveBuffer: [],
      })),

    setLastPolledAt: (ts) => set({ lastPolledAt: ts }),

    setNextPollAt: (ts) => set({ nextPollAt: ts }),

    setConsecutiveErrors: (count) => set({ consecutiveErrors: count }),

    setActiveDetailTab: (tab) => set({ activeDetailTab: tab }),

    setPiiRedactionEnabled: (enabled) => set({ piiRedactionEnabled: enabled }),

    setAutoForwardEnabled: (enabled) =>
      set({
        autoForwardEnabled: enabled,
        // Reset stats only when enabling (fresh start); preserve on disable so
        // the user can see the last session's results (including circuit breaker trips).
        ...(enabled && {
          autoForwardStats: { total: 0, succeeded: 0, failed: 0 },
        }),
      }),

    incrementAutoForwardStats: (succeeded) =>
      set((state) => ({
        autoForwardStats: {
          total: state.autoForwardStats.total + 1,
          succeeded: state.autoForwardStats.succeeded + (succeeded ? 1 : 0),
          failed: state.autoForwardStats.failed + (succeeded ? 0 : 1),
        },
      })),

    resetAutoForwardStats: () =>
      set({ autoForwardStats: { total: 0, succeeded: 0, failed: 0 } }),

    setForwardConfig: (config) => set({ forwardConfig: config }),

    setError: (error) => set({ error, isLoading: false, isLoadingMore: false }),

    setIsLoading: (loading) => set({ isLoading: loading }),

    setIsLoadingMore: (loading) => set({ isLoadingMore: loading }),

    setNextOffset: (offset) => set({ nextOffset: offset }),

    setHasMore: (hasMore) => set({ hasMore }),

    reset: () => set({ ...initialState }),
  }))
);
